import React from 'react';
import clsx from 'clsx';
import { FiCheck, FiAlertTriangle, FiXCircle, FiTool } from 'react-icons/fi';
import { ServiceStatus as ServiceStatusType, SystemStatus } from '../../types';

interface ServiceStatusProps {
  services: ServiceStatusType[];
  isLoading: boolean;
}

const statusIcon: Record<SystemStatus, React.ReactNode> = {
  operational: <FiCheck className="w-5 h-5 text-green-500" />,
  degraded: <FiAlertTriangle className="w-5 h-5 text-amber-500" />,
  outage: <FiXCircle className="w-5 h-5 text-red-500" />,
  maintenance: <FiTool className="w-5 h-5 text-blue-500" />,
};

const statusLabel: Record<SystemStatus, string> = {
  operational: 'Operational',
  degraded: 'Degraded',
  outage: 'Outage',
  maintenance: 'Maintenance',
};

const statusBgColor: Record<SystemStatus, string> = {
  operational: 'bg-green-50',
  degraded: 'bg-amber-50',
  outage: 'bg-red-50',
  maintenance: 'bg-blue-50',
};

const ServiceStatus: React.FC<ServiceStatusProps> = ({ services, isLoading }) => {
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Services</h2>
        </div>
        <div className="divide-y divide-gray-100">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="p-4 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="h-4 bg-gray-200 rounded w-32"></div>
                <div className="h-4 bg-gray-200 rounded w-20"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Services</h2>
      </div>
      <div className="divide-y divide-gray-100">
        {services.map((service) => (
          <div
            key={service.name}
            className={clsx(
              'p-4 transition-colors',
              statusBgColor[service.status]
            )}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {statusIcon[service.status]}
                <div>
                  <p className="font-medium text-gray-900">{service.name}</p>
                  {service.description && (
                    <p className="text-sm text-gray-500">{service.description}</p>
                  )}
                </div>
              </div>
              <div className="text-right">
                <span className="text-sm font-medium text-gray-700">
                  {statusLabel[service.status]}
                </span>
                {service.responseTimeMs && service.status === 'operational' && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    {service.responseTimeMs}ms
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ServiceStatus;
